import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStatus } from "../Auth/AuthStatusContext";
import { useAuth } from 'react-oidc-context';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import ProfileInfoDialog from './ProfileInfoDialog';
import ApplicantService from '../../services/ApplicantService';

// Use a static public path for the logo that works with Vite's public directory
const logoUrl = '/logo512.png';

const TitleBar: React.FC = () => {
  const navigate = useNavigate();
  const { profile, logout } = useAuthStatus();
  const auth = useAuth();

  // Get the user's email from the profile or auth user info
  const userEmail = profile?.email || auth.user?.profile?.email || 'User';
  const [applicantName, setApplicantName] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const fetchApplicantName = useCallback(async () => {
    try {
      const service = new ApplicantService();
      const info = await service.getApplicant();
      const first = info.first_name?.trim() || '';
      const last = info.last_name?.trim() || '';
      const name = [first, last].filter(Boolean).join(' ').trim();
      if (name) {
        setApplicantName(name);
      } else {
        setApplicantName(null);
      }
    } catch (error) {
      console.error('Failed to load applicant info for title bar:', error);
    }
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchApplicantName();
    }
  }, [auth.isAuthenticated, fetchApplicantName]);

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    if (auth.isAuthenticated) {
      fetchApplicantName();
    }
  };

  const displayName = applicantName || userEmail;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = () => {
    // Call the logout function from UserContext which handles token removal
    logout();
    // Close the menu
    handleClose();
    // Don't navigate here - the redirect will happen automatically from the signoutRedirect
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ minHeight: '70px', height: '70px' }}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={logoUrl}
                alt="MentoringFusion Logo"
                style={{
                  height: 50,
                  width: 50,
                  marginRight: 12
                }}
              />
              <Typography
                variant="h5"
                component={Link}
                to="/"
                sx={{
                  flexGrow: 1,
                  textDecoration: 'none',
                  color: 'white',
                  fontWeight: 'bold',
                  letterSpacing: '0.5px'
                }}
              >
                MentoringFusion
              </Typography>
            </Box>
          </Box>

          {auth.isAuthenticated && (
            <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <AccountCircleIcon />
                <Typography
                  component="span"
                  noWrap
                  variant="subtitle2"
                  sx={{
                    ml: 1,
                    maxWidth: 200,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: 'inline-block'
                  }}
                >
                  {displayName as React.ReactNode}
                </Typography>
              </IconButton>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => { setProfileDialogOpen(true); handleClose(); }}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  Profile Info
                </MenuItem>
                <MenuItem onClick={() => handleMenuClick('/location')}>
                  <ListItemIcon>
                    <LocationOnIcon fontSize="small" />
                  </ListItemIcon>
                  Location
                </MenuItem>
                <MenuItem onClick={() => handleMenuClick('/availability')}>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  Availability
                </MenuItem>
                <MenuItem onClick={() => handleMenuClick('/disc')}>
                  <ListItemIcon>
                    <PsychologyIcon fontSize="small" />
                  </ListItemIcon>
                  DISC Assessment
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <ExitToAppIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(90deg, #2B8EF8 0%, #9C45F3 100%)',
            color: 'white'
          }}>
            <img src={logoUrl} alt="MentoringFusion Logo" style={{ height: 36, width: 36 }} />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 'bold' }}>MentoringFusion</Typography>
          </Box>

          <List sx={{ flexGrow: 1, pt: 0 }}>
            <ListItem button component={Link} to="/" onClick={toggleDrawer(false)}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                secondary="View your overview"
              />
            </ListItem>
            <Divider />
          </List>
        </Box>
      </Drawer>

      <ProfileInfoDialog
        open={profileDialogOpen}
        onClose={handleProfileDialogClose}
      />
    </>
  );
};

export default TitleBar;